import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { useTranslation } from 'react-i18next';
import { collection, query, getDocs, orderBy, where } from 'firebase/firestore';
import { db } from '../../firebase';

// 捐赠者类型定义
interface Donor {
    id: string;
    name: string;
    nameEn?: string;
    amount: number;
    currency: string;
    year: number;
    project: string;
    projectEn?: string;
    level: string;
    isAnonymous: boolean;
    showInPublic: boolean;
    notes?: string;
    notesEn?: string;
}

// 捐赠级别类型
type DonorLevel = 'platinum' | 'gold' | 'silver' | 'honorable';

// 捐赠级别分类
interface DonorCategory {
    level: DonorLevel;
    title: string;
    titleEn: string;
    donors: Donor[];
}

const DonorsList: React.FC = () => {
    const { t, i18n } = useTranslation();
    const [activeTab, setActiveTab] = useState<DonorLevel>('platinum');
    const [donors, setDonors] = useState<DonorCategory[]>([]);
    const [loading, setLoading] = useState<boolean>(true);
    const [error, setError] = useState<string | null>(null);
    const isEnglish = i18n.language === 'en';

    // 获取捐赠数据
    useEffect(() => {
        const fetchDonors = async () => {
            try {
                setLoading(true);
                setError(null);
                
                // 创建查询，获取所有公开展示的捐赠记录
                const donationsQuery = query(
                    collection(db, 'donations'),
                    where('showInPublic', '==', true),
                    orderBy('amount', 'desc'),
                    orderBy('year', 'desc')
                );
                
                const querySnapshot = await getDocs(donationsQuery);
                
                // 创建捐赠级别分类
                const donorCategories: DonorCategory[] = [
                    { level: 'platinum', title: '铂金捐助者', titleEn: 'Platinum Donors ($1,000+)', donors: [] },
                    { level: 'gold', title: '黄金捐助者', titleEn: 'Gold Donors ($500+)', donors: [] },
                    { level: 'silver', title: '白银捐助者', titleEn: 'Silver Donors ($100+)', donors: [] },
                    { level: 'honorable', title: '荣誉捐助者', titleEn: 'Honorable Donors (Up to $100)', donors: [] }
                ];
                
                // 遍历查询结果并分类
                querySnapshot.forEach(doc => {
                    const data = doc.data();
                    const donorData: Donor = {
                        id: doc.id,
                        name: data.isAnonymous ? t('donate.donors.anonymous', '匿名捐赠者') : data.name,
                        nameEn: data.isAnonymous ? t('donate.donors.anonymous', 'Anonymous Donor') : data.nameEn,
                        amount: data.amount,
                        currency: data.currency,
                        year: data.year,
                        project: data.project,
                        projectEn: data.projectEn || data.project,
                        level: data.level,
                        isAnonymous: data.isAnonymous,
                        showInPublic: data.showInPublic,
                        notes: data.notes || '',
                        notesEn: data.notesEn || data.notes || ''
                    };
                    
                    // 根据level添加到对应分类
                    const category = donorCategories.find(cat => cat.level === data.level);
                    if (category) {
                        category.donors.push(donorData);
                    }
                });
                
                // 过滤掉没有捐赠者的级别
                const filteredCategories = donorCategories.filter(category => category.donors.length > 0);
                
                setDonors(filteredCategories);
                
                // 如果当前活动标签没有数据，设置为第一个有数据的标签
                if (filteredCategories.length > 0) {
                    const hasCurrentLevel = filteredCategories.some(cat => cat.level === activeTab);
                    if (!hasCurrentLevel) {
                        setActiveTab(filteredCategories[0].level);
                    }
                }
            } catch (err) {
                console.error('获取捐赠数据失败:', err);
                setError(isEnglish ? 'Failed to fetch donation data' : '获取捐赠数据失败，请稍后再试');
            } finally {
                setLoading(false);
            }
        };
        
        fetchDonors();
    }, [t, isEnglish, activeTab]);

    // 格式化金额显示
    const formatAmount = (amount: number, currency: string) => {
        let symbol = '';
        switch (currency) {
            case 'usd':
                symbol = '$';
                break;
            case 'cny':
                symbol = '¥';
                break;
            case 'cad':
                symbol = 'CA$';
                break;
            case 'eur':
                symbol = '€';
                break;
            case 'gbp':
                symbol = '£';
                break;
            default:
                symbol = '';
        }
        
        return `${symbol}${amount.toLocaleString()}`;
    };

    // 获取项目名称（根据语言）
    const getProjectName = (donor: Donor) => {
        return isEnglish && donor.projectEn ? donor.projectEn : donor.project;
    };

    // 获取捐赠者名称（根据语言）
    const getDonorName = (donor: Donor) => {
        if (donor.isAnonymous) {
            return t('donate.donors.anonymous', isEnglish ? 'Anonymous Donor' : '匿名捐赠者');
        }
        return isEnglish && donor.nameEn ? donor.nameEn : donor.name;
    };

    // 获取捐赠者备注（根据语言）
    const getDonorNotes = (donor: Donor) => {
        return isEnglish && donor.notesEn ? donor.notesEn : donor.notes;
    };

    // 获取活动标签的捐赠者
    const getActiveDonors = () => {
        const activeCategory = donors.find(category => category.level === activeTab);
        return activeCategory ? activeCategory.donors : [];
    };

    return (
        <motion.section 
            className="mb-16"
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.7 }}
        >
            <h2 className="text-3xl font-bold text-gray-900 mb-6 border-b border-gray-200 pb-2">
                {t('donate.donors.title', isEnglish ? 'Our Generous Donors' : '我们慷慨的捐助者')}
            </h2>
            
            <div className="mb-6">
                <p className="text-lg text-gray-700 mb-4">
                    {t('donate.donors.description', isEnglish ? 
                        'We are deeply grateful to the following donors for their generous contributions. Their support enables us to continually enhance the quality of education and create a better learning environment for our students.' : 
                        '我们深深感谢以下捐助者的慷慨捐赠。他们的支持使我们能够不断提高教育质量，为学生创造更好的学习环境。'
                    )}
                </p>
            </div>
            
            {/* 加载状态 */}
            {loading && (
                <div className="py-20 text-center">
                    <div className="inline-block animate-spin rounded-full h-12 w-12 border-4 border-blue-500 border-t-transparent"></div>
                    <p className="mt-4 text-lg text-gray-600">{t('common.loading', '加载中...')}</p>
                </div>
            )}
            
            {/* 错误状态 */}
            {error && (
                <div className="text-center py-20 bg-red-50 rounded-lg">
                    <p className="text-red-600 text-lg">
                        {error}
                    </p>
                    <button
                        className="mt-4 px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700 transition-colors"
                        onClick={() => window.location.reload()}
                    >
                        {t('common.retry', '重试')}
                    </button>
                </div>
            )}
            
            {/* 没有捐赠数据 */}
            {!loading && !error && donors.length === 0 && (
                <div className="text-center py-12 bg-gray-50 rounded-lg">
                    <p className="text-gray-500 text-lg">
                        {t('donate.donors.noData', '暂无捐赠数据')}
                    </p>
                </div>
            )}
            
            {/* 捐助者分类标签 */}
            {!loading && !error && donors.length > 0 && (
                <>
                    <div className="flex flex-wrap border-b border-gray-200 mb-6">
                        {donors.map((category) => (
                            <button
                                key={category.level}
                                className={`px-4 py-2 font-medium text-sm mr-2 mb-2 rounded-t-lg transition-colors duration-300 ${
                                    activeTab === category.level 
                                    ? 'bg-blue-600 text-white' 
                                    : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                                }`}
                                onClick={() => setActiveTab(category.level)}
                            >
                                {isEnglish ? category.titleEn : category.title} ({category.donors.length})
                            </button>
                        ))}
                    </div>
                    
                    {/* 捐助者列表内容 */}
                    <div className="bg-white rounded-lg shadow-md overflow-hidden">
                        <div className="overflow-x-auto">
                            <table className="min-w-full divide-y divide-gray-200">
                                <thead className="bg-gray-50">
                                    <tr>
                                        <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider w-1/6">
                                            {t('donate.donors.table.name', '捐助者')}
                                        </th>
                                        {/* <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider w-1/8">
                                            {t('donate.donors.table.amount', '金额')}
                                        </th> */}
                                        <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider w-1/8">
                                            {t('donate.donors.table.year', '年份')}
                                        </th>
                                        <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider w-1/6">
                                            {t('donate.donors.table.project', '支持项目')}
                                        </th>
                                        <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                            {t('donate.donors.table.notes', '备注')}
                                        </th>
                                    </tr>
                                </thead>
                                <tbody className="bg-white divide-y divide-gray-200">
                                    {getActiveDonors().map((donor, index) => (
                                        <tr key={donor.id} className={index % 2 === 0 ? 'bg-white' : 'bg-gray-50'}>
                                            <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                                                {getDonorName(donor)}
                                            </td>
                                            {/* <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                                                {formatAmount(donor.amount, donor.currency)}
                                            </td> */}
                                            <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                                                {donor.year}
                                            </td>
                                            <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                                                {getProjectName(donor)}
                                            </td>
                                            <td className="px-6 py-4 text-sm text-gray-500 w-full">
                                                <div className="min-h-[60px] max-h-40 overflow-y-auto pr-2">
                                                    {getDonorNotes(donor) ? (
                                                        <p className="whitespace-pre-line">{getDonorNotes(donor)}</p>
                                                    ) : (
                                                        <span className="text-gray-400 italic">{t('donate.donors.noNotes', '无备注')}</span>
                                                    )}
                                                </div>
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    </div>
                    
                    <div className="mt-6 text-center">
                        <p className="text-gray-600 italic">
                            {t('donate.donors.note', '* 仅列出获得许可公开的捐助者信息')}
                        </p>
                    </div>
                </>
            )}
        </motion.section>
    );
};

export default DonorsList; 